import React, { useCallback } from "react";
import { useDispatch } from "react-redux";
import { LessonDetails } from "@/models";
import { useAmiraMessage } from "../AmiraMessageContext";
import { makeToast, ToastTypes } from "@/components/Global/Notifications";
import { handleBothOverAndUnderbooked } from "./LessonPlanAutoFixUtils";
import "./LessonPlanAutoFix.scss";
import { useTranslation } from "react-i18next";

type OverbookedHandler = (
  studentIds: string[],
  lesson: LessonDetails,
  dispatch: any,
  skills: any[]
) => Promise<Map<string, any[]>>;

type UnderbookedHandler = (
  studentIds: string[],
  lesson: LessonDetails,
  dispatch: any,
  skills: any[],
  existingManifests?: Map<string, any[]>
) => Promise<number>;

interface LessonPlanAutoFixProps {
  getLessonData: () => {
    lesson: LessonDetails;
    skills: any[];
    overbookedStudentIds: string[];
    underbookedStudentIds: string[];
  };
  handleOverbookedStudents: OverbookedHandler;
  handleUnderbookedStudents: UnderbookedHandler;
}

const pluralize = (count: number, noun: string) =>
  `${count} ${noun}${count === 1 ? "" : "s"}`;

const createToastMessage = (
  overbookedCount: number,
  underbookedCount: number
): string | null => {
  const messages: string[] = [];

  if (overbookedCount > 0) {
    messages.push(
      `${pluralize(overbookedCount, "overbooked student")} adjusted`
    );
  }

  if (underbookedCount > 0) {
    messages.push(
      `${pluralize(underbookedCount, "underbooked student")} adjusted`
    );
  }

  return messages.length > 0 ? messages.join(", ") : null;
};

export const LessonPlanAutoFix: React.FC<LessonPlanAutoFixProps> = ({
  getLessonData,
  handleOverbookedStudents,
  handleUnderbookedStudents,
}) => {
  const dispatch = useDispatch();
  const { onToggleModal, onDismiss, alerts } = useAmiraMessage();
  const { t } = useTranslation("alerts");
  const handleConfirm = useCallback(async () => {
    // Get fresh data right before making changes
    const {
      lesson,
      skills,
      overbookedStudentIds: currentOverbooked,
      underbookedStudentIds: currentUnderbooked,
    } = getLessonData();

    let overbookedCount = 0;
    let underbookedCount = 0;

    if (currentOverbooked.length > 0 && currentUnderbooked.length > 0) {
      console.log("handleBothOverAndUnderbooked");
      const result = await handleBothOverAndUnderbooked(
        currentOverbooked,
        currentUnderbooked,
        lesson,
        dispatch,
        skills
      );
      overbookedCount = result.overbookedCount;
      underbookedCount = result.underbookedCount;
    } else if (currentOverbooked.length > 0) {
      console.log("handleOverbookedStudents");
      await handleOverbookedStudents(
        currentOverbooked,
        lesson,
        dispatch,
        skills
      );
      overbookedCount = currentOverbooked.length;
    } else if (currentUnderbooked.length > 0) {
      console.log("handleUnderbookedStudents");
      await handleUnderbookedStudents(
        currentUnderbooked,
        lesson,
        dispatch,
        skills
      );
      underbookedCount = currentUnderbooked.length;
    }

    const toastMessage = createToastMessage(overbookedCount, underbookedCount);

    if (toastMessage) {
      makeToast(toastMessage, ToastTypes.success, true, 5000);
    }

    onToggleModal(false);

    const alertForThisLesson = alerts.find((alert) =>
      alert.alertIds.includes(lesson.lessonPlanId)
    );

    if (alertForThisLesson) {
      onDismiss(alertForThisLesson.id);
    }
  }, [getLessonData, dispatch, onToggleModal, onDismiss, alerts]);

  const handleCancel = () => {
    onToggleModal(false);
  };

  const {
    overbookedStudentIds: currentOverbooked,
    underbookedStudentIds: currentUnderbooked,
  } = getLessonData();

  const totalStudents = [
    ...new Set([...currentOverbooked, ...currentUnderbooked]),
  ].length;

  return (
    <div className="lesson-plan-auto-fix">
      <p className="lesson-plan-auto-fix__description">
        {totalStudents === 1
          ? t("LessonPlanAutoFix.modal_description", {
              count: totalStudents,
            })
          : t("LessonPlanAutoFix.modal_description_grouped", {
              count: totalStudents,
            })}
      </p>

      <div className="lesson-plan-auto-fix__actions">
        <button
          className="lesson-plan-auto-fix__action-button"
          onClick={handleConfirm}
        >
          {t("LessonPlanAutoFix.confirm")}
        </button>
        <button
          className="lesson-plan-auto-fix__action-button"
          onClick={handleCancel}
        >
          {t("LessonPlanAutoFix.cancel")}
        </button>
      </div>
    </div>
  );
};
